import React, { useEffect, useRef, useState } from "react";
import { useRutas } from "../../hooks/useRutas";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import AuthImage, { abrirFicheroProtegido, fetchProtegido } from "../../components/AuthImage";
import BotonVolver from "../../components/BotonVolver";
import { leerBorradorModificar, borrarBorradorModificar, listarBorradoresModificar } from "../../utils/flujoCompra";

const estadoColores = {
  pedido: "#EE82EE",
  comprado: "#3b82f6",
  "en reparto": "#f59e0b",
  entregado: "#10b981",
  "en la puerta": "#d12b39ff",
};

// Helpers de color para routes (igual que en otros componentes)
const coloresRutas = [
  "#4ECDC4",
  "#4CAF50",
  "#556270",
  "#C7F464",
  "#C44DFF",
  "#FFB347",
];
const getContrastColor = (hex) => {
  if (!hex) return '#000';
  const c = hex.replace('#','');
  const r = parseInt(c.substring(0,2),16);
  const g = parseInt(c.substring(2,4),16);
  const b = parseInt(c.substring(4,6),16);
  const lum = 0.2126*r + 0.7152*g + 0.0722*b;
  return lum > 150 ? '#000' : '#fff';
};
const darkenColor = (hex, percent) => {
  if (!hex) return hex;
  const c = hex.replace('#','');
  const num = parseInt(c,16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  r = Math.max(0, Math.min(255, Math.round(r * (100 - percent) / 100)));
  g = Math.max(0, Math.min(255, Math.round(g * (100 - percent) / 100)));
  b = Math.max(0, Math.min(255, Math.round(b * (100 - percent) / 100)));
  const rr = r.toString(16).padStart(2,'0');
  const gg = g.toString(16).padStart(2,'0');
  const bb = b.toString(16).padStart(2,'0');
  return `#${rr}${gg}${bb}`;
};

const ModificarVerCompras = () => {
  const [compras, setCompras] = useState([]);
  // Estado específico para la carga de la lista de compras (no bloqueará toda la UI)
  const [loadingCompras, setLoadingCompras] = useState(true);
  const [error, setError] = useState(null);
  const [countsByEstado, setCountsByEstado] = useState({});
  const [pisos, setPisos] = useState([]);
  const [cargandoPisos, setCargandoPisos] = useState(false);
  const [compraMostrandoSelector, setCompraMostrandoSelector] = useState(null); // id de compra para la que se muestra el selector
  // Modo de cambio de piso: 'sin' (mover a un piso libre, actual), 'con' (intercambiar con otro que ya tenga compra)
  const [modoCambioPiso, setModoCambioPiso] = useState('sin');
  const [loadingOperacion, setLoadingOperacion] = useState(false);

  const [imagenZoom, setImagenZoom] = useState(null);
  // Set con ids de compras cuyo ticket se está mostrando (lazy load)
  const [ticketsVisibles, setTicketsVisibles] = useState(() => new Set());
  // Set de compras con la foto de "en la puerta" visible
  const [fotosPuertaVisibles, setFotosPuertaVisibles] = useState(() => new Set());

  const { user } = useAuth();
  const usuario = user?.user || user;

  const navigate = useNavigate();
  const location = useLocation();

  const modo = location.state?.modo || "ver";

  // Estados para filtros
  const [filtroAbreviatura, setFiltroAbreviatura] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("");
  // Si estamos en modo modificar por defecto queremos ver solo 'pedido'
  const [filtroEstado, setFiltroEstado] = useState(modo === 'modificar' ? 'pedido' : 'todos');
  const [filtroRuta, setFiltroRuta] = useState("");
  const { rutas, loading: loadingRutas } = useRutas();

  // Normalize route strings (remove diacritics, trim). We'll use this both for
  // building the stable rutasUnicas list and for matching when assigning colors.
  const normalizeRoute = (r) => {
    try {
      return (r == null ? '' : String(r)).normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
    } catch (e) {
      return (r == null ? '' : String(r)).trim();
    }
  };

  // Build candidate lists in order of preference: facetas (`rutas`), pisos, then compras.
  // This ensures the filter shows the global known routes first (from backend facetas),
  // and still falls back to local data if necessary.
  const rutasFromPisos = Array.isArray(pisos) ? pisos.filter(p => p != null).map(p => p.ruta).filter(r => r != null) : [];
  const rutasFromCompras = Array.isArray(compras) ? compras.filter(c => c != null).map(c => c.ruta_piso ?? c.ruta).filter(r => r != null) : [];
  const candidates = [];
  if (Array.isArray(rutas) && rutas.length > 0) candidates.push(...rutas);
  if (rutasFromPisos.length > 0) candidates.push(...rutasFromPisos);
  if (rutasFromCompras.length > 0) candidates.push(...rutasFromCompras);

  const rutasUnicas = Array.from(new Set(candidates
    .map(x => normalizeRoute(typeof x === 'object' ? (x.ruta ?? x.value ?? x.id ?? '') : x))
    .filter(s => s !== '')))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  // Asignador de color estable por ruta
  const obtenerColorRuta = (ruta) => {
    if (ruta === null || ruta === undefined || String(ruta).trim() === '') return '#888';
    const norm = normalizeRoute(ruta);
    const index = rutasUnicas.findIndex(x => x === norm);
    if (index === -1) return coloresRutas[0] || '#888';
    return coloresRutas[index % coloresRutas.length] || '#888';
  };

  // Paginación
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    if (!usuario) return;
    fetchCompras(usuario, 1);
    fetchPisos(usuario);
  }, [usuario]);

  useEffect(() => {
    // Marcamos en sessionStorage que estamos en esta página
    sessionStorage.setItem("enModificarVerCompras", "true");

    // Detectar recarga de página
    if (sessionStorage.getItem("enModificarVerCompras") === "true") {
      // Limpiamos la marca
      sessionStorage.removeItem("enModificarVerCompras");
      // Opcional: redirigir automáticamente al recargar
      // navigate("/supervisora");
    }
  }, [navigate]);

  const fetchCompras = async (usuario, pagina) => {
    setLoadingCompras(true);
    try {
      const params = new URLSearchParams();
      params.set('user_supervisora', usuario);
      params.set('page', pagina);
      params.set('pageSize', pageSize);
      if (filtroAbreviatura) params.set('abreviatura', filtroAbreviatura);
      // uso diaPedidoInicio/diaPedidoFin para filtrar por la fecha seleccionada (misma convención que admin)
      if (filtroFecha) { params.set('diaPedidoInicio', filtroFecha); params.set('diaPedidoFin', filtroFecha); }
      if (filtroEstado && filtroEstado !== 'todos') params.set('estado', filtroEstado);
      if (filtroRuta) params.set('ruta', filtroRuta);

      const url = `/api/modificarVerCompras/compras?${params.toString()}`;
      const res = await axios.get(url);
      const items = res.data.items || [];
  setCompras(items);
      // DEBUG: log items that contain en_la_puerta_img so we can verify front receives the filename
      try {
        console.log('[ModificarVerCompras] fetched items with en_la_puerta_img:', items
          .filter(it => it && it.en_la_puerta_img)
          .map(it => ({ id: it.id, estado: it.estado, en_la_puerta_img: it.en_la_puerta_img })));
      } catch (e) { /* ignore logging errors */ }
      // No mostrar por defecto las fotos en la puerta: usar el botón "Ver foto en la puerta"
      // (comportamiento igual que en admin VerCompras)
      setFotosPuertaVisibles(new Set());
      setTotal(res.data.total);
      setCountsByEstado(res.data.countsByEstado || {});
      // Recordar qué página acabamos de cargar para que la UI refleje correctamente
      setPage(pagina);
      setLoadingCompras(false);
    } catch (err) {
      setError("❌ Error al cargar las compras");
      setLoadingCompras(false);
    }
  };

  const fetchPisos = async (usuario) => {
    setCargandoPisos(true);
    try {
      const res = await axios.get(`/api/pisoCrearCompras/pisos_supervisora?user=${encodeURIComponent(usuario)}`);
      // Puede venir 404 si no hay pisos libres; lo tratamos silenciosamente
      if (Array.isArray(res.data)) setPisos(res.data);
    } catch (e) {
      // no bloqueamos la vista si falla
    } finally {
      setCargandoPisos(false);
    }
  };

  const cambiarPisoCompra = async (compra, nuevoIdPiso, nuevaAbreviatura) => {
    if (!nuevoIdPiso) return;
    const confirma = window.confirm(`¿Confirmas mover la compra ${compra.id} del piso ${compra.abreviatura_piso} al piso ${nuevaAbreviatura}?`);
    if (!confirma) return;
    setLoadingOperacion(true);
    try {
      const payload = {
        id_compra: compra.id,
        nuevo_id_piso: nuevoIdPiso,
        nueva_abreviatura_piso: nuevaAbreviatura,
      };
      const res = await axios.post(`/api/modificarVerCompras/cambiar_piso`, payload);
      if (res.data && res.data.success) {
        alert('✅ Piso cambiado correctamente');
        // Actualizamos en memoria sin refetch completo: buscamos y mutamos.
        setCompras(prev => prev.map(c => c.id === compra.id ? {
          ...c,
          id_piso: res.data.nuevo_id_piso,
          abreviatura_piso: res.data.nueva_abreviatura_piso,
          dia_pedido: res.data.dia_pedido,
        } : c));
        setCompraMostrandoSelector(null);
      } else {
        alert('❌ No se pudo cambiar el piso');
      }
    } catch (e) {
      alert('❌ Error al cambiar el piso');
    } finally {
      setLoadingOperacion(false);
    }
  };

  // Intercambiar compras entre dos pisos (compra actual y otra compra existente)
  const intercambiarComprasEntrePisos = async (compraOrigen, compraDestino) => {
    if (!compraOrigen || !compraDestino) return;
    const confirma = window.confirm(`¿Confirmas INTERCAMBIAR la compra ${compraOrigen.id} (${compraOrigen.abreviatura_piso}) con la compra ${compraDestino.id} (${compraDestino.abreviatura_piso})?`);
    if (!confirma) return;
    setLoadingOperacion(true);
    try {
      const payload = { id_compra_a: compraOrigen.id, id_compra_b: compraDestino.id };
      const res = await axios.post(`/api/modificarVerCompras/intercambiar_pisos`, payload);
      if (res.data?.success) {
        alert('✅ Compras intercambiadas correctamente');
        setCompras(prev => prev.map(c => {
          if (c.id === compraOrigen.id) {
            return { ...c, id_piso: res.data.compra_a.id_piso, abreviatura_piso: res.data.compra_a.abreviatura_piso, dia_pedido: res.data.compra_a.dia_pedido };
          }
            if (c.id === compraDestino.id) {
            return { ...c, id_piso: res.data.compra_b.id_piso, abreviatura_piso: res.data.compra_b.abreviatura_piso, dia_pedido: res.data.compra_b.dia_pedido };
          }
          return c;
        }));
        setCompraMostrandoSelector(null);
        setModoCambioPiso('sin');
      } else {
        alert('❌ No se pudo intercambiar');
      }
    } catch (e) {
      alert('❌ Error al intercambiar compras');
    } finally {
      setLoadingOperacion(false);
    }
  };

  // PAGINACIÓN
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Reset to page 1 when filters change. Do NOT reset on total changes, otherwise
  // a successful fetch that updates `total` can unintentionally force us back to page 1.
  useEffect(() => { setPage(1); }, [filtroAbreviatura, filtroFecha, filtroEstado]);
  // Refetch when filtros cambian (debounced: wait 1s after last keystroke).
  // Se salta la primera ejecución (montaje): esa carga inicial ya la hace el
  // efecto de arriba de inmediato, sin esperar 1s; repetirla aquí solo
  // duplicaba la misma petición a la vez en cada entrada a la página.
  const primerFiltroRef = useRef(true);
  useEffect(() => {
    if (!usuario) return;
    if (primerFiltroRef.current) {
      primerFiltroRef.current = false;
      return;
    }
    const t = setTimeout(() => fetchCompras(usuario, 1), 1000);
    return () => clearTimeout(t);
  }, [usuario, filtroAbreviatura, filtroFecha, filtroEstado, filtroRuta]);
  const handlePageChange = (newPage) => {
    if (!usuario) return;
    window.scrollTo(0, 0);
    fetchCompras(usuario, newPage);
  };

  const irAEditarCompra = (compra) => {
    navigate(`/supervisora/crearCompras/${compra.id_piso}`, {
      state: {
        id: compra.id,
        user: usuario,
        id_piso: compra.id_piso,
        abreviatura: compra.abreviatura_piso,
        productos: compra.productos,
        notas_supervisora: compra.notas_supervisora,
        user_supervisora: compra.user_supervisora,
        n_estudiantes: compra.n_estudiantes,
        // Flag para que CrearCompras sepa que viene de Modificar y NO debe persistir localStorage al volver atrás
        desdeModificar: true,
      },
    });
  };

  // Descarga un PDF de una compra concreta (basado en el snippet proporcionado)
  const descargarCompraPDF = (compra) => {
    try {
      const pageHeight = 297; // A4 en mm
      const marginTop = 20;
      const lineHeight = 10;
      const bottomMargin = 20;
      const maxY = pageHeight - bottomMargin;

      const doc = new jsPDF();
      doc.setFontSize(18);
      let y = marginTop;
      doc.setFont('helvetica','bold');

      let abreviatura_piso_sin_cajetin = compra.abreviatura_piso.split("(")[0];

      const info_piso = `Piso ID: ${compra.id_piso} - ${abreviatura_piso_sin_cajetin}`;
      doc.text(info_piso, 10, y); y += 15;

      doc.setFontSize(14);
      const fechaPedido = compra.dia_pedido || '';
      doc.text(`Fecha pedido: ${fechaPedido}`, 10, y); y += lineHeight;
      doc.text(`Franja horaria: ${compra.franja_horaria || '-'}`, 10, y); y += lineHeight;
      if (compra.user_supervisora) { doc.text(`Supervisora: ${compra.user_supervisora}`, 10, y); y += lineHeight; }

      if (compra.notas_supervisora && String(compra.notas_supervisora).trim() !== '') {
        y += 4;
        doc.text('Notas de la supervisora:', 10, y);
        y += 8;
        const maxWidth = 180;
        const paddingH = 5;
        const paddingW = 5;
        const notasWrapped = doc.splitTextToSize(String(compra.notas_supervisora), maxWidth - paddingW*2);
        const blockHeight = notasWrapped.length * lineHeight + paddingH * 2;
        if (y + blockHeight > maxY) { doc.addPage(); y = marginTop; }
        doc.rect(10, y, maxWidth, blockHeight);
        doc.setFont('helvetica','normal');
        let textY = y + paddingH + 2;
        notasWrapped.forEach(linea => { if (textY > maxY) { doc.addPage(); textY = marginTop; } doc.text(linea, 10 + paddingW, textY); textY += lineHeight; });
        y += blockHeight + 6;
      } else {
        doc.setFont('helvetica','normal');
      }

      y += lineHeight;
      (compra.productos || []).slice().sort((a,b)=> (a.nombre||'').localeCompare(b.nombre||''))
        .forEach(p => { if (y > maxY) { doc.addPage(); y = marginTop; } doc.text(`- ${p.nombre} (Cantidad: ${p.cantidad})`, 10, y); y += lineHeight; });

      const fechaFormateada = String(fechaPedido).replace(/\//g,'-').replace(/[: ]/g,'_');
      doc.save(`Compra_${compra.id}_${compra.abreviatura_piso}_${fechaFormateada}.pdf`);
    } catch (e) { alert('No se pudo generar el PDF de la compra.'); }
  };

  function formatearFecha(fechaStr) {
    if (!fechaStr) return "";

    const fechaISO = fechaStr.replace(" ", "T");
    const fecha = new Date(fechaISO);

    if (isNaN(fecha)) {
      return fechaStr;
    }

    const dia = String(fecha.getDate()).padStart(2, "0");
    const mes = String(fecha.getMonth() + 1).padStart(2, "0");
    const anio = fecha.getFullYear();

    const horas = String(fecha.getHours()).padStart(2, "0");
    const minutos = String(fecha.getMinutes()).padStart(2, "0");

    return `${dia}-${mes}-${anio} ${horas}:${minutos}`;
  }

  const handleEliminarCompra = async (idCompra) => {
    const confirmar = window.confirm("¿Estás seguro de que quieres eliminar esta compra?");
    if (!confirmar) return;

    try {
      // Guardamos referencia antes de mutar el estado para poder limpiar flags
      const compraEliminada = compras.find(c => c.id === idCompra);
      await axios.delete(`/api/modificarVerCompras/eliminar_compra/${idCompra}`);
      alert("✅ Compra eliminada correctamente");
      setCompras((prev) => prev.filter((c) => c.id !== idCompra));
      if (compraEliminada) {
        const idPisoEliminado = compraEliminada.id_piso;
        // Limpiar flags de flujo bloqueado para permitir nueva compra
        sessionStorage.removeItem(`compraConfirmada_${idPisoEliminado}`);
        sessionStorage.removeItem(`crearCompras_token_${idPisoEliminado}`);
        localStorage.removeItem(`compraData_piso_${idPisoEliminado}`);
      }
      borrarBorradorModificar(idCompra);
      setBorradores(listarBorradoresModificar());
    } catch (err) {
      alert("❌ Error al eliminar la compra.");
    }
  };

  // Asegura que compras siempre es un array
  const comprasSafe = Array.isArray(compras) ? compras : [];

  // Cambios sin confirmar (borradores de modificar guardados en el móvil): se avisa en
  // rojo arriba y en cada compra. Si una compra ya no está en "pedido" (el comprador la
  // ha avanzado), sus cambios ya no se pueden aplicar: se quitan.
  const [borradores, setBorradores] = useState(() => listarBorradoresModificar());
  useEffect(() => {
    let cambiado = false;
    comprasSafe.forEach((c) => {
      if (c.estado && c.estado !== "pedido" && leerBorradorModificar(c.id)) { borrarBorradorModificar(c.id); cambiado = true; }
    });
    if (cambiado) setBorradores(listarBorradoresModificar());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compras]);
  const borradorDe = (idCompra) => borradores.find((b) => String(b.id) === String(idCompra));
  const horaDe = (ts) => (ts ? new Date(ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "");
  const fechaHoraDe = (ts) => {
    if (!ts) return "";
    const d = new Date(ts), hoy = new Date();
    const mismoDia = d.toDateString() === hoy.toDateString();
    return mismoDia ? `de las ${horaDe(ts)}` : `del ${d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })} a las ${horaDe(ts)}`;
  };

  // No hacemos return temprano: mostramos la interfaz completa siempre.
  // El error (si existe) se muestra en un banner localizado y el loader solo en la lista de compras.
  return (
  <div style={{ maxWidth: "700px", margin: "0 auto", padding: "20px", fontSize: "18px", fontFamily: "Arial, sans-serif", paddingBottom: 100 }}>
      <style>{`@keyframes spin { from {transform:rotate(0deg);} to {transform:rotate(360deg);} } @keyframes pulseBadge { 0%,100% { transform: scale(1); box-shadow:0 4px 14px rgba(255,152,0,0.55);} 50% { transform: scale(1.08); box-shadow:0 6px 22px rgba(255,152,0,0.7);} } details summary::-webkit-details-marker { display: none; } details summary::marker { display: none; } details summary { position: relative; } details.cambios-details summary::before { content: '▶'; position: absolute; left: 0; transition: transform 0.2s; font-size: 11px; } details.cambios-details[open] summary::before { transform: rotate(90deg); }`}</style>
      <BotonVolver fallback="/supervisora/compras" />
      <h2 style={{ textAlign: "center", fontSize: "24px", marginBottom: "20px", color: "#333" }}>
        {modo === "modificar" ? "Modificar Compras" : "Ver Compras"}
      </h2>

      {modo === "modificar" && borradores.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff1f2", border: "1.5px solid #fecdd3", borderLeft: "6px solid #e11d48", borderRadius: 12, padding: "8px 12px", margin: "0 auto 14px", maxWidth: 520 }}>
          <span style={{ minWidth: 28, height: 28, padding: "0 8px", borderRadius: 999, background: "#e11d48", color: "#fff", fontWeight: 900, fontSize: 15, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {borradores.length}
          </span>
          <div style={{ lineHeight: 1.3, textAlign: "left" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#9f1239" }}>
              {borradores.length === 1 ? "Compra con cambios sin confirmar" : "Compras con cambios sin confirmar"}
            </div>
            <div style={{ fontSize: 12.5, color: "#be123c" }}>Pulsa «Continuar» en la compra y confírmala en el carrito</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "20px",
          justifyContent: "center",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Filtrar por abreviatura"
          value={filtroAbreviatura}
          onChange={(e) => setFiltroAbreviatura(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "16px",
            height: "40px",
            boxSizing: "border-box",
            border: "1px solid #ccc",
            borderRadius: "4px",
            outline: "none",
            fontFamily: "inherit",
            flexShrink: 0,
            minWidth: "160px",
          }}
        />
        <div style={{ position: 'relative', minWidth: '160px', flexShrink: 0 }}>
          <input
            type="date"
            value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "16px",
              height: "40px",
              boxSizing: "border-box",
              border: "1px solid #ccc",
              borderRadius: "4px",
              outline: "none",
              fontFamily: "inherit",
              flexShrink: 0,
              minWidth: "160px",
              background: 'transparent',
              position: 'relative',
              zIndex: 2
            }}
          />
          {!filtroFecha && (
            <span style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#aaa',
              pointerEvents: 'none',
              fontSize: 16,
              zIndex: 1
            }}>
              Día del pedido
            </span>
          )}
        </div>
        <select
          value={filtroRuta}
          onChange={e => setFiltroRuta(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "16px",
            height: "40px",
            boxSizing: "border-box",
            borderRadius: "4px",
            outline: "none",
            fontFamily: "inherit",
            flexShrink: 0,
            minWidth: "160px",
            background: filtroRuta ? obtenerColorRuta(filtroRuta) : '#fff',
            color: filtroRuta ? getContrastColor(obtenerColorRuta(filtroRuta)) : '#888',
            border: filtroRuta ? `1px solid ${darkenColor(obtenerColorRuta(filtroRuta),25)}` : '1px solid #ccc',
          }}
        >
          <option value="">Filtrar por ruta</option>
          {loadingRutas && <option disabled>Cargando rutas...</option>}
          {rutasUnicas.map(r => (
            <option key={r} value={r} style={{ backgroundColor: obtenerColorRuta(r), color: getContrastColor(obtenerColorRuta(r)) }}>{r}</option>
          ))}
        </select>
        {modo === "ver" && (
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "16px",
              height: "40px",
              boxSizing: "border-box",
              border: "1px solid #ccc",
              borderRadius: "4px",
              outline: "none",
              fontFamily: "inherit",
              flexShrink: 0,
              minWidth: "160px",
              color: estadoColores[filtroEstado] || "#000",
            }}
          >
            <option value="todos" style={{ color: estadoColores["todos"] }}>Todos los estados</option>
            <option value="pedido" style={{ color: estadoColores["pedido"] }}>Pedido</option>
            <option value="comprado" style={{ color: estadoColores["comprado"] }}>Comprado</option>
            <option value="en reparto" style={{ color: estadoColores["en reparto"] }}>En reparto</option>
            <option value="entregado" style={{ color: estadoColores["entregado"] }}>Entregado</option>
            <option value="en la puerta" style={{ color: estadoColores["en la puerta"] }}>En la puerta</option>
          </select>
        )}
      </div>

      {/* Banner de error localizado (no bloquea toda la UI) */}
      {error && (
        <div style={{ width: '100%', textAlign: 'center', margin: '8px 0 18px', padding: '8px 12px', borderRadius: 8, background: '#fff0f0', color: '#b00020', border: '1px solid #f5c6cb' }}>
          {error}
        </div>
      )}


      {/* Resumen de compras totales y por estado */}
      <div style={{
        width: '100%',
        margin: '0 auto 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <span style={{
          color:'#1976d2',
          background:'#e3f2fd',
          borderRadius:10,
          padding:'6px 22px',
          border:'1.5px solid #90caf9',
          fontSize: '18px',
          fontWeight: 800,
          letterSpacing:'1px',
          marginBottom: 6,
          textTransform:'uppercase',
          boxShadow:'0 1px 6px #1976d211',
        }}>TOTAL: {total}</span>
        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center',
          fontSize: 14,
          fontWeight: 600,
        }}>
          {Object.entries(estadoColores).map(([estado, color]) => {
            if (estado === 'todos') return null;
            const count = (countsByEstado && countsByEstado[estado] != null) ? countsByEstado[estado] : comprasSafe.filter(c => c.estado === estado).length;
            if (!count) return null;
            // Colores más suaves para fondo y texto
            let bg, fg;
            switch(estado) {
              case 'pedido': bg = '#ffe4ef'; fg = '#e96390ff'; break; // rosa suave
              case 'comprado': bg = '#e3f2fd'; fg = '#1976d2'; break;
              case 'en reparto': bg = '#fff7e6'; fg = '#f59e0b'; break;
              case 'entregado': bg = '#e8f5e9'; fg = '#388e3c'; break;
              case 'en la puerta': bg = '#fdeaea'; fg = '#d12b39'; break;
              default: bg = '#f5f5f5'; fg = color;
            }
            return (
              <span key={estado} style={{
                color: fg,
                background: bg,
                borderRadius: 8,
                padding: '4px 10px',
                border: `1.2px solid ${fg}33`,
                fontWeight: 700,
                textShadow: 'none',
                boxShadow: 'none',
                letterSpacing: '0.5px',
              }}>{estado.charAt(0).toUpperCase() + estado.slice(1)}: {count}</span>
            );
          })}
        </div>
      </div>

      {/* Paginación fija abajo */}
      {total > pageSize && (
        <div style={{
          position: 'fixed',
          left: 0,
          bottom: 'var(--fichaje-h, 0px)',
          width: '100vw',
          background: 'rgba(255,255,255,0.97)',
          borderTop: '2px solid #e0e0e0',
          boxShadow: '0 -2px 12px #0001',
          zIndex: 200,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 15,
          padding: '12px 0',
        }}>
          <button onClick={()=> { if(page>1) handlePageChange(page-1); }} disabled={page===1} style={{padding:'7px 16px',borderRadius:8,border:'1.5px solid #1976d2',background:page===1?'#e3e3e3':'#1976d2',color:page===1?'#888':'#fff',fontWeight:800,cursor:page===1?'not-allowed':'pointer'}}>◀ Anterior</button>
          <span style={{color:'#1976d2',fontWeight:900,letterSpacing:'1px'}}>Página {page} / {totalPages}</span>
          <span style={{color:'#555',fontWeight:700}}>{total} compras</span>
          <button onClick={()=> { if(page<totalPages) handlePageChange(page+1); }} disabled={page===totalPages} style={{padding:'7px 16px',borderRadius:8,border:'1.5px solid #1976d2',background:page===totalPages?'#e3e3e3':'#1976d2',color:page===totalPages?'#888':'#fff',fontWeight:800,cursor:page===totalPages?'not-allowed':'pointer'}}>Siguiente ▶</button>
        </div>
      )}

      {/* Indicador de carga / vacío solo para la lista */}
      {loadingCompras && comprasSafe.length === 0 && (
        <p style={{ textAlign: "center", marginTop: "30px", fontSize: "18px", color: "#666" }}>
          Cargando compras...
        </p>
      )}
      {!loadingCompras && comprasSafe.length === 0 && (
        <p style={{ textAlign: "center", marginTop: "30px", fontSize: "18px", color: "#666" }}>
          No hay compras para mostrar.
        </p>
      )}

  {comprasSafe.map((compra) => {
    // Colores de fondo suaves según estado
    const fondosEstado = {
      pedido: '#fff3ff',
      comprado: '#e3f2fd',
      'en reparto': '#fff7e6',
      entregado: '#e8f5e9',
      'en la puerta': '#fadbdb',
    };
    const estadoColor = estadoColores[compra.estado] || '#555';
    const fondoEstado = fondosEstado[compra.estado] || '#fff';

    return (
  <div
    key={compra.id}
    style={{
      border: "1px solid #ddd",
      borderLeft: `6px solid ${estadoColor}`,
      borderRadius: "10px",
      padding: "10px 12px",
      marginBottom: "10px",
      backgroundColor: fondoEstado,
      fontSize: "15px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
      transition: "box-shadow 0.3s ease",
      position: 'relative',
    }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)"}
    onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.08)"}
  >
    {modo === "modificar" && borradorDe(compra.id) && (() => {
      const b = borradorDe(compra.id);
      return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", borderRadius: 999, padding: "3px 10px", fontSize: 12.5, fontWeight: 800, marginBottom: 8, maxWidth: "calc(100% - 140px)" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#e11d48", flexShrink: 0 }} />
          Sin confirmar · {b.n_productos} prod. · {Number(b.precio_total || 0).toFixed(2).replace(".", ",")} € · {fechaHoraDe(b.ts).replace(/^de las |^del /, "")}
        </div>
      );
    })()}
    {/* Estado arriba derecha */}
    <div style={{position:'absolute',top:18,right:20,zIndex:2}}>
      <div style={{display:'flex',alignItems:'center',gap:'10'}}>
        <span
          style={{
            whiteSpace: "nowrap",
            fontWeight: "750",
            color: estadoColores[compra.estado] || "#555",
            textTransform: "capitalize",
            border: `2.5px solid ${estadoColores[compra.estado] || "#555"}`,
            padding: "3px 14px",
            borderRadius: "14px",
            fontSize: "16px",
            userSelect: "none",
            backgroundColor: "#f3f3f3",
            boxShadow: `0 0 8px ${estadoColores[compra.estado] || "#555"}22`,
            transition: "all 0.3s ease",
            letterSpacing: '0.5px',
          }}
        >
          {compra.estado}
        </span>
      </div>
    </div>
    {/* Fila principal: abreviatura a la izquierda, a la derecha precio_total, franja_horaria, n_bolsas */}
      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-start',gap:'6px',marginBottom:'2px'}}>
      {/* Abreviatura piso solo borde azul, letras azul y fuente estilizada */}
      <span
        style={{
          fontWeight: "800",
          fontSize: "19px",
          color: "#1976d2",
          background: "transparent",
          border: "2px solid #1976d2",
          borderRadius: "8px",
          padding: "5px 16px",
          fontFamily: "'Montserrat', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          letterSpacing: "1.1px",
          whiteSpace:'nowrap',
          textShadow: '0 1px 4px #1976d211',
          boxShadow: 'none',
          display: 'inline-block',
          textTransform: 'uppercase',
        }}
      >
        {compra.abreviatura_piso}
      </span>
        {/* Badge de ruta (mostrar ruta aunque venga en campos distintos) */}
        <span style={{ marginLeft: 8 }}>
          { (compra.ruta_piso || compra.ruta) ? (
            (() => {
              const ruta = compra.ruta_piso || compra.ruta;
              const colorRuta = obtenerColorRuta(ruta);
              return (
                <span style={{
                  background: colorRuta,
                  color: getContrastColor(colorRuta),
                  padding: '3px 10px',
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 700,
                  border: `1px solid ${darkenColor(colorRuta, 25)}`
                }}>Ruta: {ruta}</span>
              );
            })()
          ) : (
            <span style={{
              background: '#f3f3f3',
              color: '#666',
              padding: '3px 10px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              border: '1px solid #ddd'
            }}>Sin ruta</span>
          )}
        </span>
      <div style={{display:'flex',alignItems:'center',gap:'18px',flexWrap:'wrap',marginTop:'2px'}}>
        <span
          style={{
            fontWeight: "800",
            fontSize: "20px",
            color: "#388e3c",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            textShadow: '0 2px 8px #388e3c11',
          }}
        >
          {compra.precio_final.toFixed(2)} €
        </span>
        {compra.franja_horaria && (
          <span style={{
            fontWeight: 800,
            fontSize: 16,
            color: '#64748b',
            background: '#f1f5f9',
            borderRadius: 9,
            padding: '3px 10px',
            marginLeft: 4,
            letterSpacing: '0.5px',
            border: '2px solid #cbd5e1',
            display: 'inline-block',
            boxShadow: '0 2px 8px #64748b11',
          }}>
            [{compra.franja_horaria}]
          </span>
        )}
        {(compra.n_bolsas !== undefined && compra.n_bolsas !== null) && (
          <div
            title="Número de bolsas"
            style={{
              background:'linear-gradient(135deg,#faf3e0,#ffe4b5)',
              color:'#4a3420',
              padding:'6px 14px 7px',
              borderRadius:'10px',
              border:'2px solid #e0c79a',
              display:'flex',
              flexDirection:'column',
              alignItems:'center',
              justifyContent:'center',
              minWidth:40,
              fontFamily:"'Segoe UI',sans-serif",
              lineHeight:1.05,
              boxShadow:'0 2px 8px rgba(0,0,0,0.09)'
            }}
          >
            <span style={{fontSize:18,fontWeight:800}}>{compra.n_bolsas} 🛍️</span>
          </div>
        )}
        <button
          title="Descargar compra en PDF"
          aria-label="Descargar compra en PDF"
          onClick={() => descargarCompraPDF(compra)}
          style={{
            border: "none",
            background: "#1976d2",
            color: "#fff",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(25,118,210,0.18)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            userSelect: "none",
            fontSize: '15px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px) scale(1.08)";
            e.currentTarget.style.boxShadow = "0 6px 15px rgba(25,118,210,0.28)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0) scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 6px rgba(25,118,210,0.18)";
          }}
        >
          📥
        </button>
      </div>
    </div>
    {/* Franja horaria duplicada eliminada */}

    {/* Cambio 2: Fecha pedido más estética (sin emoticono) */}
    <div
      style={{
        fontSize: "16px",
        color: "#555",
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        backgroundColor: "#e3f2fd",
        borderRadius: "8px",
        padding: "8px 14px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontWeight: "600",
        boxShadow: "inset 0 0 8px #90caf9aa",
        maxWidth: "fit-content",
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span>Pedido: {formatearFecha(compra.dia_pedido)}</span>
        {compra.dia_compra && (
          <span style={{ fontWeight: 500, color: '#0d47a1' }}>
            Compra: {compra.user_comprador} - {formatearFecha(compra.dia_compra)}
          </span>
        )}
        {!compra.dia_compra && compra.estado === 'pedido' && (
          <span style={{ fontWeight: 500, color: '#0d47a1' }}>
            Compra: {compra.user_comprador || '—'}
          </span>
        )}
        {!compra.dia_recogida && compra.estado === 'en reparto' && (
          <span style={{ fontWeight: 500, color: '#ca6f1aff' }}>
            Preparando reparto... {compra.user_repartidor || '—'}
          </span>
        )}
        {compra.dia_recogida && (
          <span style={{ fontWeight: 500, color: '#ca6f1aff' }}>
            En reparto: {compra.user_repartidor} - {formatearFecha(compra.dia_recogida)}
          </span>
        )}
        {compra.dia_reparto && (
          <span style={{ fontWeight: 500, color: '#2d7a09ff' }}>
            Reparto: {compra.user_repartidor} - {formatearFecha(compra.dia_reparto)}
          </span>
        )}
      </span>
      {/* Botón de intercambio: a la derecha del bloque de fecha (el de descarga se movió arriba, junto a las bolsas) */}
      {modo === 'modificar' && compra.estado === 'pedido' && (
        <button
          title="Intercambiar con otra compra"
          aria-label="Intercambiar compra"
          onClick={() => { setCompraMostrandoSelector(compra.id); setModoCambioPiso('con'); }}
          style={{
            border: 'none',
            background: '#1e88e5',
            color: '#fff',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            cursor: 'pointer',
            boxShadow: '0 4px 10px rgba(30,136,229,0.3)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            marginLeft: 6,
            fontSize: 16,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(30,136,229,0.45)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(30,136,229,0.3)'; }}
        >
          🔁
        </button>
      )}
    </div>

    {/* Cambio 3: palabra "Productos" más estética */}
    <details style={{ fontSize: "16px" }}>
      <summary
        style={{
          cursor: "pointer",
          fontWeight: "700",
          marginBottom: "12px",
          fontSize: "20px",
          color: "#1976d2",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          userSelect: "none",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="#1976d2"
          style={{ width: 24, height: 24 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M7 6h10M7 18h10" />
        </svg>
        Productos
      </summary>

      {/* Cambio 4: tabla más estética y columna producto más ancha */}
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: "0 8px",
          marginTop: "8px",
          fontSize: "15px",
          color: "#444",
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: "left",
                padding: "10px 12px",
                width: "50%",
                backgroundColor: "#f5f5f5",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
            >
              Producto
            </th>
            <th
              style={{
                textAlign: "center",
                padding: "10px 12px",
                width: "15%",
                backgroundColor: "#f5f5f5",
              }}
            >
              Cant.
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "10px 12px",
                width: "20%",
                backgroundColor: "#f5f5f5",
              }}
            >
              Precio unidad
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "10px 12px",
                width: "15%",
                backgroundColor: "#f5f5f5",
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
  {compra.productos.map((producto, idx) => {
    // Comprobamos si este producto fue eliminado
    const cambioEliminado = compra.cambios.find(c => c.id_producto_eliminado === producto.id);
    if (cambioEliminado) {
      // Si id_producto_añadido === 1 => solo tachado (eliminado sin sustitución)
      return (
        <tr key={idx} style={{ color: "#999" }}>
          <td style={{ padding: "6px 12px", textDecoration: "line-through" }}>{cambioEliminado.nombre_eliminado}</td>
          <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{cambioEliminado.cantidad_eliminado}</td>
          <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{cambioEliminado.precio_eliminado.toFixed(2)} €</td>
          <td style={{ textDecoration: "line-through", textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
            {(cambioEliminado.cantidad_eliminado * cambioEliminado.precio_eliminado).toFixed(2)} €
          </td>
        </tr>
      );
    }

    // Comprobamos si este producto fue añadido
    const cambioAñadido = compra.cambios.find(c => c.nombre_añadido === producto.nombre);
    if (cambioAñadido) {
      return (
        <tr key={idx} style={{ color: "#2e7d32" }}>
          <td style={{ padding: "6px 12px" }}>{cambioAñadido.nombre_añadido}</td>
          <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambioAñadido.unidades_añadido}</td>
          <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambioAñadido.precio_añadido.toFixed(2)} €</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
            {(cambioAñadido.unidades_añadido * cambioAñadido.precio_añadido).toFixed(2)} €
          </td>
        </tr>
      );
    }

    // Productos normales
    return (
      <tr key={idx}>
        <td style={{ padding: "6px 12px" }}>• {producto.nombre}</td>
        <td style={{ padding: "6px 12px", textAlign: "right" }}>{producto.cantidad}</td>
        <td style={{ padding: "6px 12px", textAlign: "right" }}>{producto.precio.toFixed(2)} €</td>
        <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
          {(producto.cantidad * producto.precio).toFixed(2)} €
        </td>
      </tr>
    );
  })}

  {/* Añadidos que no estén en productos normales */}
  {compra.cambios.map((cambio, idx) => {
    // Si es un "no sustitución" (id_producto_añadido === 1), no mostrar producto añadido
    if (cambio.id_producto_añadido === 1) return null;
    if (!compra.productos.find(p => p.nombre === cambio.nombre_añadido)) {
      return (
        <tr key={`nuevo-${idx}`} style={{ color: "#2e7d32" }}>
          <td style={{ padding: "6px 12px" }}>{cambio.nombre_añadido}</td>
          <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambio.unidades_añadido}</td>
          <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambio.precio_añadido.toFixed(2)} €</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
            {(cambio.unidades_añadido * cambio.precio_añadido).toFixed(2)} €
          </td>
        </tr>
      );
    }
    return null;
  })}
</tbody>

      </table>
            {/* Notas supervisora, comprador y precio comprador */}
            {(compra.user_supervisora || compra.notas_supervisora?.trim() || compra.notas_comprador?.trim() || compra.precio_comprador) && (
              <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
                {compra.notas_supervisora?.trim() && (
                  <div
                    style={{
                      backgroundColor: "#f3e8ff",
                      border: "1px solid #d8b4fe",
                      borderRadius: "8px",
                      padding: "10px 15px",
                      color: "#6b21a8",
                      fontSize: "15px",
                      lineHeight: "1.4",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      userSelect: "text",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <strong>Notas supervisora:</strong> {compra.notas_supervisora}
                  </div>
                )}
                {compra.precio_comprador && (
                  <div
                    style={{
                      backgroundColor: "#e8f5e9",
                      border: "1px solid #a5d6a7",
                      borderRadius: "8px",
                      padding: "10px 15px",
                      color: "#2e7d32",
                      fontWeight: "700",
                      fontSize: "16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      userSelect: "text",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Precio comprador: {parseFloat(compra.precio_comprador).toFixed(2)} €
                  </div>
                )}
              </div>
            )}   
          </details>

          {/* Desplegable de cambios */}
          {Array.isArray(compra.cambios) && compra.cambios.length > 0 && (
            <details className="cambios-details" style={{
              marginTop: 12,
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
              border: '1.5px solid #ffc107',
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(255,193,7,0.15)',
            }}>
              <summary style={{
                fontSize: 14,
                fontWeight: 700,
                color: '#856404',
                marginBottom: 0,
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                userSelect: 'none',
                listStyle: 'none',
                paddingLeft: 20,
              }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span>Cambios realizados ({compra.cambios.length})</span>
              </summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                {compra.cambios.map((cambio, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: '8px 12px',
                    background: '#fff',
                    borderRadius: 8,
                    border: '1px solid #e0e0e0',
                    fontSize: 13,
                  }}>
                    {cambio.id_producto_eliminado && cambio.id_producto_eliminado !== 1 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        color: '#d32f2f',
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>❌</span>
                        <span style={{ textDecoration: 'line-through', flex: 1 }}>
                          {cambio.nombre_eliminado}
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          {cambio.cantidad_eliminado} ud.
                        </span>
                        <span style={{ fontWeight: 700 }}>
                          {Number(cambio.precio_eliminado || 0).toFixed(2)} €
                        </span>
                      </div>
                    )}
                    {cambio.id_producto_añadido && cambio.id_producto_añadido !== 1 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        color: '#2e7d32',
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>✅</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>
                          {cambio.nombre_añadido}
                        </span>
                        <span style={{ fontWeight: 600 }}>
                          {cambio.unidades_añadido} ud.
                        </span>
                        <span style={{ fontWeight: 700 }}>
                          {Number(cambio.precio_añadido || 0).toFixed(2)} €
                        </span>
                      </div>
                    )}
                    {cambio.id_producto_añadido === 1 && (
                      <div style={{
                        fontSize: 11,
                        fontStyle: 'italic',
                        color: '#666',
                        marginTop: 2,
                      }}>
                        (No sustituido)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {compra.notas_comprador?.trim() && (
            <div
              style={{
                backgroundColor: "#edccf5ff",
                border: "1px solid #e664f1ff",
                borderRadius: "8px",
                padding: "10px 15px",
                color: "#57075aff",
                fontSize: "15px",
                lineHeight: "1.4",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                userSelect: "text",
                whiteSpace: "pre-wrap",
              }}
            >
              <strong>Notas comprador:</strong> {compra.notas_comprador}
            </div>
          )}

          {/* Ticket (lazy load) */}
            {compra.ticket && (
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                {ticketsVisibles.has(compra.id) ? (
                  <>
                    <AuthImage
                      src={`/api/tickets/${compra.ticket}`}
                      alt="Ticket de compra"
                      style={{
                        maxWidth: "160px",
                        cursor: "pointer",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      }}
                      onClick={() =>
                        setImagenZoom(`/api/tickets/${compra.ticket}`)
                      }
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.07)";
                        e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                      }}
                    />
                    <button
                      style={{
                        padding: "8px 18px",
                        fontSize: "15px",
                        fontWeight: "600",
                        cursor: "pointer",
                        borderRadius: "30px",
                        border: "none",
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.5)",
                        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                        userSelect: "none",
                      }}
                      onClick={async () => {
                        try {
                          const urlTicket = `/api/tickets/${compra.ticket}`;
                          const response = await fetchProtegido(urlTicket);
                          if (!response.ok) throw new Error("No se pudo descargar el archivo");
                          const blob = await response.blob();
                          const urlBlob = window.URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = urlBlob;
                          link.download = `ticket_${compra.id}_${compra.abreviatura_piso}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (error) {
                          alert("Error al descargar el ticket: " + error.message);
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#1565c0";
                        e.currentTarget.style.boxShadow = "0 6px 18px rgba(21, 101, 192, 0.7)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#1976d2";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.5)";
                      }}
                    >
                      ⬇ Descargar ticket
                    </button>
                    <button
                      style={{
                        padding: "8px 18px",
                        fontSize: "15px",
                        fontWeight: "600",
                        cursor: "pointer",
                        borderRadius: "30px",
                        border: "none",
                        backgroundColor: "#e53935",
                        color: "#fff",
                        boxShadow: "0 4px 12px rgba(229,57,53,0.5)",
                        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                        userSelect: "none",
                      }}
                      onClick={() => setTicketsVisibles(prev => { const n=new Set(prev); n.delete(compra.id); return n; })}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#c62828";
                        e.currentTarget.style.boxShadow = "0 6px 18px rgba(198, 40, 40, 0.7)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#e53935";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(229,57,53,0.5)";
                      }}
                    >Ocultar ticket</button>
                  </>
                ) : (
                  <button
                    style={{
                      padding: "8px 18px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: "pointer",
                      borderRadius: "30px",
                      border: "none",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.5)",
                      transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                      userSelect: "none",
                    }}
                    onClick={() => setTicketsVisibles(prev => { const n=new Set(prev); n.add(compra.id); return n; })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#1565c0";
                      e.currentTarget.style.boxShadow = "0 6px 18px rgba(21, 101, 192, 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#1976d2";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.5)";
                    }}
                  >Ver ticket</button>
                )}

                {/* Modal imagen zoom (se muestra si hay imagen ampliada) */}
                {imagenZoom && (
                  <div
                    onClick={() => setImagenZoom(null)}
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                      cursor: "zoom-out",
                      padding: "12px",
                    }}
                  >
                    <AuthImage
                      src={imagenZoom}
                      alt="Ticket ampliado"
                      style={{
                        maxHeight: "90vh",
                        maxWidth: "90vw",
                        borderRadius: "10px",
                        boxShadow: "0 0 24px rgba(255,255,255,0.95)",
                      }}
                    />
                  </div>
                )}
              </div>
            )}

          {/* Foto en la puerta (mostrar/ocultar) - igual que en admin: solo si estado === 'en la puerta' y hay imagen */}
          {compra.estado === 'en la puerta' && compra.en_la_puerta_img && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {fotosPuertaVisibles.has(compra.id) ? (
                <>
                  <AuthImage
                    src={`/api/entregas_fotos/${encodeURIComponent(compra.en_la_puerta_img)}`}
                    alt="Foto en la puerta"
                    style={{
                      maxWidth: "180px",
                      cursor: "pointer",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    }}
                    onClick={() => abrirFicheroProtegido(`/api/entregas_fotos/${encodeURIComponent(compra.en_la_puerta_img)}`)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.07)";
                      e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                    }}
                  />
                  <button
                    style={{
                      padding: "8px 18px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: "pointer",
                      borderRadius: "30px",
                      border: "none",
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.5)",
                      transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                      userSelect: "none",
                    }}
                    onClick={async () => {
                      try {
                        const url = `/api/entregas_fotos/${encodeURIComponent(compra.en_la_puerta_img)}`;
                        const r = await fetchProtegido(url);
                        if (!r.ok) throw new Error('Descarga fallida');
                        const b = await r.blob();
                        const u = URL.createObjectURL(b);
                        const a = document.createElement('a');
                        a.href = u;
                        a.download = compra.en_la_puerta_img || `foto_en_puerta_${compra.id}.jpg`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      } catch (err) {
                        alert('Error al descargar la foto');
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#1565c0";
                      e.currentTarget.style.boxShadow = "0 6px 18px rgba(21, 101, 192, 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#1976d2";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.5)";
                    }}
                  >
                    ⬇ Descargar foto
                  </button>
                  <button
                    style={{
                      padding: "8px 18px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: "pointer",
                      borderRadius: "30px",
                      border: "none",
                      backgroundColor: "#e53935",
                      color: "#fff",
                      boxShadow: "0 4px 12px rgba(229,57,53,0.5)",
                      transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                      userSelect: "none",
                    }}
                    onClick={() => setFotosPuertaVisibles((prev) => { const n=new Set(prev); n.delete(compra.id); return n; })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#c62828";
                      e.currentTarget.style.boxShadow = "0 6px 18px rgba(198, 40, 40, 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#e53935";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(229,57,53,0.5)";
                    }}
                  >Ocultar foto</button>
                </>
              ) : (
                <button
                  style={{
                    padding: "8px 18px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    borderRadius: "30px",
                    border: "none",
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.5)",
                    transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                    userSelect: "none",
                  }}
                  onClick={() => setFotosPuertaVisibles((prev) => { const n=new Set(prev); n.add(compra.id); return n; })}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#1565c0";
                    e.currentTarget.style.boxShadow = "0 6px 18px rgba(21, 101, 192, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#1976d2";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.5)";
                  }}
                >Ver foto en la puerta</button>
              )}

              {/* Modal zoom ya existente se reutiliza */}
              {imagenZoom && (
                <div
                  onClick={() => setImagenZoom(null)}
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                    cursor: "zoom-out",
                    padding: "12px",
                  }}
                >
                  <AuthImage
                    src={imagenZoom}
                    alt="Foto ampliada"
                    style={{
                      maxHeight: "90vh",
                      maxWidth: "90vw",
                      borderRadius: "10px",
                      boxShadow: "0 0 24px rgba(255,255,255,0.95)",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Botones editar/eliminar solo en modo modificar */}
          {modo === "modificar" && (
            <div
              style={{
                marginTop: "15px",
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => irAEditarCompra(compra)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: borradorDe(compra.id) ? "#dc2626" : "#4caf50",
                  border: "none",
                  borderRadius: "6px",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = borradorDe(compra.id) ? "#b91c1c" : "#388e3c")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = borradorDe(compra.id) ? "#dc2626" : "#4caf50")}
              >
                {borradorDe(compra.id) ? "✏️ Continuar" : "Editar"}
              </button>

              <button
                onClick={() => handleEliminarCompra(compra.id)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f44336",
                  border: "none",
                  borderRadius: "6px",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#d32f2f")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f44336")}
              >
                Eliminar
              </button>
            </div>
          )}
        </div>
      );
    })}
    {/* OVERLAY selector de pisos a pantalla casi completa */}
  {compraMostrandoSelector && (() => {
        const compraSel = comprasSafe.find(c => c.id === compraMostrandoSelector);
        if (!compraSel) return null;
        const pisosDisponibles = pisos.filter(p => p.id !== compraSel.id_piso); // pisos sin compra (asumido por backend actual)
        // Compras distintas a la actual para modo intercambio
  const otrasCompras = comprasSafe.filter(c => c.id !== compraSel.id && c.estado === 'pedido');
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setCompraMostrandoSelector(null); }}
            style={{
              position:'fixed',
              top:0,
              left:0,
              width:'100vw',
              height:'100vh',
              background:'rgba(0,0,0,0.55)',
              zIndex:1000,
              display:'flex',
              justifyContent:'center',
              alignItems:'center',
              padding:'20px'
            }}
          >
            <div style={{
              background:'#fff',
              width:'95%',
              maxWidth:'1000px',
              height:'85%',
              borderRadius:'18px',
              boxShadow:'0 8px 28px rgba(0,0,0,0.35)',
              display:'flex',
              flexDirection:'column',
              overflow:'hidden',
              /* Movido solo un poco arriba (-40px) manteniendo centro horizontal */
              transform:'translateY(-40px)'
            }}>
              <div style={{
                padding:'18px 22px',
                borderBottom:'1px solid #ddd',
                display:'flex',
                alignItems:'center',
                justifyContent:'space-between',
                gap:'18px',
                flexWrap:'wrap',
                background: 'linear-gradient(90deg,#ffb74d,#ffa726)'
              }}>
                <h3 style={{margin:0,color:'#222',fontSize:'22px',display:'flex',alignItems:'center',gap:'12px'}}>
                  Cambiar piso compra {compraSel.id} ({compraSel.abreviatura_piso})
                  {loadingOperacion && (
                    <span style={{fontSize:'14px',fontWeight:600,color:'#d84315',display:'flex',alignItems:'center',gap:6}}>
                      <span className="spinner" style={{width:16,height:16,border:'3px solid #ffccbc',borderTop:'3px solid #d84315',borderRadius:'50%',display:'inline-block',animation:'spin 0.9s linear infinite'}} />
                      Procesando...
                    </span>
                  )}
                </h3>
                <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                  <button
                    onClick={() => setModoCambioPiso('sin')}
                    style={{
                      padding:'6px 10px',
                      borderRadius:'6px',
                      border: modoCambioPiso==='sin' ? '2px solid #ef6c00' : '1px solid #ccc',
                      background: modoCambioPiso==='sin' ? '#ffe0b2' : '#fff',
                      fontWeight:600,
                      cursor:'pointer',
                      minWidth:'140px',
                      fontSize:'14px'
                    }}
                    disabled={loadingOperacion}
                    title="Mover a un piso libre (sin compra)"
                  >SIN compra</button>
                  <button
                    onClick={() => setModoCambioPiso('con')}
                    style={{
                      padding:'6px 10px',
                      borderRadius:'6px',
                      border: modoCambioPiso==='con' ? '2px solid #1e88e5' : '1px solid #ccc',
                      background: modoCambioPiso==='con' ? '#bbdefb' : '#fff',
                      fontWeight:600,
                      cursor:'pointer',
                      minWidth:'140px',
                      fontSize:'14px'
                    }}
                    disabled={loadingOperacion}
                    title="Intercambiar con otro piso que ya tiene compra"
                  >CON compra</button>
                </div>
              </div>
              <div style={{
                padding:'16px 20px',
                flex:1,
                overflowY:'auto',
                WebkitOverflowScrolling:'touch'
              }}>
                {modoCambioPiso==='sin' && (
                  <>
                    <p style={{marginTop:0,fontWeight:600}}>Selecciona un piso LIBRE (sin compra) para mover la compra:</p>
                    {cargandoPisos && <p style={{opacity:0.8}}>Cargando pisos...</p>}
                    {!cargandoPisos && pisosDisponibles.length === 0 && (
                      <p style={{opacity:0.7}}>No hay pisos libres disponibles.</p>
                    )}
                    {!cargandoPisos && pisosDisponibles.length > 0 && (
                      <div style={{
                        display:'grid',
                        gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',
                        gap:'16px'
                      }}>
                        {pisosDisponibles.map(p => (
                          <button
                            key={p.id}
                            onClick={() => cambiarPisoCompra(compraSel, p.id, p.abreviatura)}
                            style={{
                              background:'#f7f7f7',
                              border:'2px solid #ff9800',
                              borderRadius:'14px',
                              padding:'18px 12px',
                              fontSize:'20px',
                              fontWeight:700,
                              cursor:'pointer',
                              display:'flex',
                              flexDirection:'column',
                              alignItems:'center',
                              justifyContent:'center',
                              transition:'all .25s',
                              boxShadow:'0 4px 10px rgba(0,0,0,0.15)'
                            }}
                            disabled={loadingOperacion}
                            onMouseEnter={e => {
                              e.currentTarget.style.background='#ffe0b2';
                              e.currentTarget.style.transform='translateY(-3px)';
                              e.currentTarget.style.boxShadow='0 8px 18px rgba(0,0,0,0.25)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background='#f7f7f7';
                              e.currentTarget.style.transform='translateY(0)';
                              e.currentTarget.style.boxShadow='0 4px 10px rgba(0,0,0,0.15)';
                            }}
                          >
                            {p.abreviatura}
                            <span style={{marginTop:'6px',fontSize:'14px',fontWeight:500,opacity:0.8}}>ID {p.id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {modoCambioPiso==='con' && (
                  <>
                    <p style={{marginTop:0,fontWeight:600}}>Selecciona OTRA compra para intercambiar los pisos (ambas se cruzarán):</p>
                    {otrasCompras.length === 0 && <p style={{opacity:0.7}}>No hay otras compras para intercambiar.</p>}
                    {otrasCompras.length > 0 && (
                      <div style={{
                        display:'grid',
                        gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',
                        gap:'16px'
                      }}>
                        {otrasCompras.map(c => (
                          <button
                            key={c.id}
                            onClick={() => intercambiarComprasEntrePisos(compraSel, c)}
                            style={{
                              background:'#f5faff',
                              border:'2px solid #2196f3',
                              borderRadius:'14px',
                              padding:'14px 10px',
                              fontSize:'16px',
                              fontWeight:600,
                              cursor:'pointer',
                              display:'flex',
                              flexDirection:'column',
                              alignItems:'flex-start',
                              justifyContent:'flex-start',
                              gap:'4px',
                              textAlign:'left',
                              transition:'all .25s',
                              boxShadow:'0 4px 10px rgba(0,0,0,0.15)'
                            }}
                            disabled={loadingOperacion}
                            onMouseEnter={e => {
                              e.currentTarget.style.background='#e3f2fd';
                              e.currentTarget.style.transform='translateY(-3px)';
                              e.currentTarget.style.boxShadow='0 8px 18px rgba(0,0,0,0.25)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background='#f5faff';
                              e.currentTarget.style.transform='translateY(0)';
                              e.currentTarget.style.boxShadow='0 4px 10px rgba(0,0,0,0.15)';
                            }}
                          >
                            <strong style={{fontSize:'18px'}}>{c.abreviatura_piso}</strong>
                            <span style={{fontSize:'13px',opacity:0.85}}>Compra #{c.id}</span>
                            {/* Estado oculto según requisito */}
                            <span style={{fontSize:'13px',opacity:0.85}}>Total: {c.precio_final.toFixed(2)} €</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{
                padding:'12px 18px',
                borderTop:'1px solid #ddd',
                display:'flex',
                justifyContent:'flex-end',
                gap:'10px',
                background:'#fafafa'
              }}>
                <button
                  onClick={() => setCompraMostrandoSelector(null)}
                  style={{
                    background:'#e0e0e0',
                    border:'1px solid #ccc',
                    padding:'10px 18px',
                    borderRadius:'8px',
                    cursor:'pointer',
                    fontWeight:600
                  }}
                >Cancelar</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ModificarVerCompras;
